import React, { useEffect, useState } from 'react';
import { Config } from 'config/envConfig';
import {
    faCaretDown,
    faCaretUp,
    faCopy,
    faRotateLeft,
    faTrash,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextArea, Button, alert, AlertType, Input } from '@quyva/shared-ui';
import axios from 'axios';
import { getUrl } from '../../../../utils';

export const PromptView = ({
    index,
    prompt,
    displayPromptName,
    instruction,
    onInstructionChange,
    onPromptNameChange,
    formatProcessName,
    selectedProcess,
    promptNameEditable = false,
    instructionEditable = true,
    instructionDisabledReason = '',
    allowCopy = false,
    onCopy,
    allowReset = true,
    showResetButton = true,
    showDeleteButton = false,
    onDelete,
    autoExpand = false,
    promptNameError = '',
    selectedPrompt,
    setSelectedPrompt,
    showWarning = false,
    warningMessage = '',
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [loadingDefault, setLoadingDefault] = useState(false);
    const [isBaseline, setIsBaseline] = useState(null);

    const effectivePromptName = displayPromptName || prompt;

    useEffect(() => {
        if (autoExpand) {
            setIsExpanded(true);
            setSelectedPrompt?.(formatProcessName(effectivePromptName));
        }
    }, [autoExpand, effectivePromptName, formatProcessName]);

    const rowsForTextArea = instruction
        ? Math.max(3, instruction.split('\n').length)
        : 3;

    const fetchBaseline = async () => {
        setLoadingDefault(true);
        try {
            const url = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/get-instruction`
            );

            const response = await axios.get(url, {
                params: {
                    process_enum: selectedProcess,
                    prompt_value: prompt,
                },
            });

            return response?.data?.instruction ?? '';
        } catch (err) {
            alert(AlertType.ERROR, 'Error fetching instruction.');
            console.error('fetchBaseline error', err);
            return null;
        } finally {
            setLoadingDefault(false);
        }
    };

    const handleReset = async () => {
        if (!allowReset) return;
        const baseline = await fetchBaseline();
        if (baseline === null) {
            alert(AlertType.ERROR, 'Could not fetch default instruction.');
            return;
        }
        if (baseline === instruction) {
            setIsBaseline(true);
            alert(AlertType.WARNING, 'Default instruction already set.');
        } else {
            onInstructionChange(baseline);
            setIsBaseline(true);
        }
    };

    return (
        <div className='w-full max-w-full sm:max-w-2xl md:max-w-4xl lg:max-w-full mx-auto my-3'>
            {/* HEADER */}
            <div
                onClick={() => {
                    setIsExpanded((prev) => !prev);
                    setSelectedPrompt(formatProcessName(effectivePromptName));
                }}
                className={`${isExpanded ? 'rounded-b-none' : ''} ${
                    selectedPrompt === formatProcessName(effectivePromptName)
                        ? 'bg-backgrounds-primary100'
                        : 'bg-backgrounds-white'
                }
                    w-full flex items-center justify-between px-3 py-2 border rounded-xl hover:shadow-inner
                    hover:border-backgrounds-primary200 hover:bg-backgrounds-primary100
                    text-texts-secondary300 cursor-pointer

                `}
            >
                <div className='flex items-center justify-between'>
                    {/* Increase heading size */}
                    <div className='flex items-center gap-2'>
                        <div
                            className={`text-base font-semibold ${
                                selectedPrompt ===
                                formatProcessName(effectivePromptName)
                                    ? 'text-texts-tertiary'
                                    : ''
                            }`}
                        >
                            {index + 1}. {formatProcessName(effectivePromptName)}
                        </div>
                        {showWarning && !!warningMessage && (
                            <div
                                className='text-icons-warning'
                                title={warningMessage}
                            >
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </div>
                        )}
                    </div>
                </div>
                <div className='mr-2 flex items-center gap-2 flex-nowrap'>
                    <Button
                        icon={isExpanded ? faCaretUp : faCaretDown}
                        onClick={(e) => {
                            setIsExpanded(!isExpanded);
                            e.stopPropagation();
                        }}
                        disabled={false}
                        hoverText='Reset to default prompt'
                        type={'warning'}
                    />
                    {allowCopy && (
                        <Button
                            icon={faCopy}
                            onClick={() => {
                                onCopy?.();
                            }}
                            hoverText='Copy prompt'
                            type={'warning'}
                        />
                    )}
                    {showDeleteButton && (
                        <Button
                            icon={faTrash}
                            onClick={() => {
                                onDelete?.();
                            }}
                            disabled={false}
                            hoverText={'Delete prompt'}
                            type={'danger'}
                        />
                    )}
                    {showResetButton && (
                        <Button
                            icon={faRotateLeft}
                            onClick={handleReset}
                            disabled={!allowReset || loadingDefault || isBaseline}
                            hoverText={
                                !instructionEditable && instructionDisabledReason
                                    ? instructionDisabledReason
                                    : 'Reset to default prompt'
                            }
                            type={'success'}
                        />
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className='w-full bg-backgrounds-white border border-t-0 rounded-b-xl p-4 text-texts-secondary300 shadow-sm'>
                    {promptNameEditable && (
                        <div className='mb-3'>
                            <div className='text-base font-semibold text-texts-secondary400 pl-1 mb-1'>
                                Prompt Name
                            </div>
                            <Input
                                type='text'
                                value={prompt ?? ''}
                                onChange={(e) => {
                                    onPromptNameChange?.(e.target.value);
                                    setIsBaseline(null);
                                }}
                                className={`w-full text-base ${
                                    promptNameError
                                        ? 'border border-texts-red400'
                                        : ''
                                }`}
                            />
                            {!!promptNameError && (
                                <div className='text-sm text-texts-red400 pl-2 mt-1'>
                                    {promptNameError}
                                </div>
                            )}
                        </div>
                    )}

                    {/* TITLE ROW */}
                    <div className='flex items-center justify-between mb-2'>
                        <div className='flex items-center gap-2'>
                            {/* Slightly bigger label */}
                            <div className='text-base font-semibold text-texts-secondary400 pl-1'>
                                <span title={instructionDisabledReason || ''}>
                                    Process Instruction
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* TEXTAREA with bigger font */}
                    <TextArea
                        rows={rowsForTextArea}
                        value={instruction ?? ''}
                        onChange={(e) => {
                            onInstructionChange(e.target.value);
                            setIsBaseline(false);
                        }}
                        className={`w-full text-base ${
                            !instructionEditable ? 'cursor-not-allowed' : ''
                        }`}
                        disabled={!instructionEditable}
                        hoverText={instructionDisabledReason || ''}
                    />
                </div>
            )}
        </div>
    );
};
